import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Button } from '@/components/ui/button';
import { Users, Shuffle, Plus, Minus, Scale, Ruler, Target, TrendingUp, Check, Trophy, Save, X, ChevronDown, ChevronUp, History, Trash2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Position values for balancing (1-5)
const POSITION_VALUES = {
  'pg': 1, 'sg': 2, 'sf': 3, 'pf': 4, 'c': 5,
};

const POSITION_NAMES = {
  'pg': 'PG', 'sg': 'SG', 'sf': 'SF', 'pf': 'PF', 'c': 'C',
};

export default function TeamBuilder({ 
  eventId, 
  signedUpUsers, 
  allUsers, 
  isAdmin = false,
  onClose 
}) {
  const { user, role } = useAuth();
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [guestPlayers, setGuestPlayers] = useState([]); // [{name: 'Gast 1', tempId: 'guest_1'}]
  const [teams, setTeams] = useState([]);
  const [criteria, setCriteria] = useState({
    height: true, weight: true, position: true, oce: false
  });
  const [playerStats, setPlayerStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [newGuestName, setNewGuestName] = useState('');
  
  // Game tracking
  const [games, setGames] = useState([]);
  const [currentGame, setCurrentGame] = useState(null);
  const [savingGames, setSavingGames] = useState(false);
  
  // Saved games from database for this event
  const [savedGames, setSavedGames] = useState([]);
  const [loadingSavedGames, setLoadingSavedGames] = useState(true);
  const [expandedGame, setExpandedGame] = useState(null);
  const [showSavedGames, setShowSavedGames] = useState(true);

  // Load player stats (OCE)
  useEffect(() => {
    const loadStats = async () => {
      try {
        const statsSnap = await getDocs(collection(db, 'game_stats'));
        const stats = {};
        
        statsSnap.docs.forEach(doc => {
          const data = doc.data();
          const playerId = data.player_id;
          
          if (!stats[playerId]) {
            stats[playerId] = { games: 0, totalOCE: 0 };
          }
          
          const points = Number(data.points) || 0;
          const rebounds = Number(data.rebounds) || 0;
          const assists = Number(data.assists) || 0;
          const steals = Number(data.steals) || 0;
          const blocks = Number(data.blocks) || 0;
          const turnovers = Number(data.turnovers) || 0;
          const fouls = Number(data.fouls) || 0;
          const minutes = Number(data.minutes) || 1;
          
          const oce = ((points + rebounds * 1.2 + assists * 1.5 + steals * 2 + blocks * 2 - turnovers * 1.5 - fouls * 0.5) / minutes) * 10;
          
          stats[playerId].games++;
          stats[playerId].totalOCE += oce;
        });
        
        Object.keys(stats).forEach(playerId => {
          stats[playerId].avgOCE = stats[playerId].totalOCE / stats[playerId].games;
        });
        
        setPlayerStats(stats);
      } catch (err) {
        console.error('Error loading stats:', err);
      }
      setLoading(false);
    };
    
    loadStats();
  }, []);

  // Load saved games for this event from backend
  useEffect(() => {
    const loadSavedGames = async () => {
      if (!eventId) {
        setLoadingSavedGames(false);
        return;
      }
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/training/games/by_event/${eventId}`);
        const data = await response.json();
        
        if (data.ok && data.games) {
          console.log('[TeamBuilder] Loaded saved games:', data.games.length);
          setSavedGames(data.games);
        }
      } catch (err) {
        console.error('[TeamBuilder] Error loading saved games:', err);
      }
      setLoadingSavedGames(false);
    };
    
    loadSavedGames();
  }, [eventId]);

  // Initialize selected players with guests from signups
  useEffect(() => {
    const initialPlayers = signedUpUsers
      .filter(s => (s.roles || []).includes('training') && !s.is_out)
      .map(s => s.user_id);
    setSelectedPlayers(initialPlayers);
    
    // Also collect guests from signups
    const guests = [];
    signedUpUsers.forEach(s => {
      if ((s.guests || []).length > 0) {
        s.guests.forEach((g, idx) => {
          guests.push({
            tempId: `guest_${s.user_id}_${idx}`,
            name: g.name || `Gast von ${allUsers.find(u => u.id === s.user_id)?.display_name?.split(' ')[0] || '?'}`,
            invitedBy: s.user_id
          });
        });
      }
    });
    setGuestPlayers(guests);
  }, [signedUpUsers, allUsers]);

  // Helper: Get shortened name with last name initial (e.g., "Guido T.")
  const getShortName = (fullName) => {
    if (!fullName) return '?';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}.`;
  };

  // Get player data with all attributes (including guests)
  const playersWithData = useMemo(() => {
    const regularPlayers = selectedPlayers.map(playerId => {
      const user = allUsers.find(u => u.id === playerId);
      const stats = playerStats[playerId];
      const fullName = user?.display_name || user?.name || 'Unbekannt';
      
      return {
        id: playerId,
        name: fullName,
        shortName: getShortName(fullName),
        height: user?.height || 175,
        weight: user?.weight || 75,
        position: user?.position || 'sf',
        oce: stats?.avgOCE || 10,
        hasData: !!(user?.height && user?.weight && user?.position),
        isGuest: false
      };
    });
    
    // Add guests with default values
    const guestData = guestPlayers.map(guest => ({
      id: guest.tempId,
      name: guest.name,
      shortName: getShortName(guest.name),
      height: 175,
      weight: 75,
      position: 'sf',
      oce: 10,
      hasData: false,
      isGuest: true
    }));
    
    return [...regularPlayers, ...guestData];
  }, [selectedPlayers, guestPlayers, allUsers, playerStats]);

  // Calculate player score
  const calculateScore = (player) => {
    let score = 0;
    let factors = 0;
    
    if (criteria.height) {
      score += ((player.height - 160) / 50) * 100;
      factors++;
    }
    if (criteria.weight) {
      score += ((player.weight - 50) / 70) * 100;
      factors++;
    }
    if (criteria.position) {
      score += (POSITION_VALUES[player.position] / 5) * 100;
      factors++;
    }
    if (criteria.oce) {
      score += (player.oce / 30) * 100;
      factors++;
    }
    
    return factors > 0 ? score / factors : 50;
  };

  // Generate balanced teams with EQUAL player distribution
  const generateTeams = () => {
    if (playersWithData.length < 4) {
      alert('Mindestens 4 Spieler benötigt!');
      return;
    }

    // Determine number of teams: 2 teams for <=12, 3 teams for >=13
    const numTeams = playersWithData.length >= 13 ? 3 : 2;
    
    // Calculate target sizes for each team
    const baseSize = Math.floor(playersWithData.length / numTeams);
    const remainder = playersWithData.length % numTeams;
    const targetSizes = Array.from({ length: numTeams }, (_, i) => 
      baseSize + (i < remainder ? 1 : 0)
    );
    
    // Sort players by the primary criterion
    const scoredPlayers = playersWithData
      .map(p => ({ ...p, score: calculateScore(p) }))
      .sort((a, b) => b.score - a.score);
    
    // Initialize teams
    const newTeams = Array.from({ length: numTeams }, () => ({
      players: [], totalScore: 0, totalOCE: 0, totalHeight: 0, totalWeight: 0,
      avgHeight: 0, avgWeight: 0, avgOCE: 0, positions: {}
    }));
    
    // Simple snake draft for initial distribution
    let teamIdx = 0;
    let direction = 1;
    
    scoredPlayers.forEach((player) => {
      // Find team with room
      while (newTeams[teamIdx].players.length >= targetSizes[teamIdx]) {
        teamIdx += direction;
        if (teamIdx >= numTeams) {
          teamIdx = numTeams - 1;
          direction = -1;
        } else if (teamIdx < 0) {
          teamIdx = 0;
          direction = 1;
        }
      }
      
      newTeams[teamIdx].players.push(player);
      newTeams[teamIdx].totalOCE += player.oce;
      newTeams[teamIdx].totalHeight += player.height;
      newTeams[teamIdx].totalWeight += player.weight;
      
      teamIdx += direction;
      if (teamIdx >= numTeams) {
        teamIdx = numTeams - 1;
        direction = -1;
      } else if (teamIdx < 0) {
        teamIdx = 0;
        direction = 1;
      }
    });
    
    // Calculate initial averages
    const updateAverages = (teams) => {
      teams.forEach(team => {
        if (team.players.length > 0) {
          team.avgOCE = team.totalOCE / team.players.length;
          team.avgHeight = team.totalHeight / team.players.length;
          team.avgWeight = team.totalWeight / team.players.length;
        }
      });
    };
    
    updateAverages(newTeams);
    
    // Optimization phase: Try swapping players to improve balance
    if (criteria.oce || criteria.height || criteria.weight) {
      let improved = true;
      let iterations = 0;
      const maxIterations = 100;
      
      while (improved && iterations < maxIterations) {
        improved = false;
        iterations++;
        
        // Calculate current imbalance
        const getCurrentImbalance = () => {
          let imbalance = 0;
          if (criteria.oce) {
            const avgOCEs = newTeams.map(t => t.avgOCE);
            imbalance += Math.max(...avgOCEs) - Math.min(...avgOCEs);
          }
          if (criteria.height) {
            const avgHeights = newTeams.map(t => t.avgHeight);
            imbalance += (Math.max(...avgHeights) - Math.min(...avgHeights)) * 0.01;
          }
          if (criteria.weight) {
            const avgWeights = newTeams.map(t => t.avgWeight);
            imbalance += (Math.max(...avgWeights) - Math.min(...avgWeights)) * 0.01;
          }
          return imbalance;
        };
        
        const currentImbalance = getCurrentImbalance();
        
        // Try swapping each pair of players between teams
        for (let t1 = 0; t1 < numTeams && !improved; t1++) {
          for (let t2 = t1 + 1; t2 < numTeams && !improved; t2++) {
            for (let p1 = 0; p1 < newTeams[t1].players.length && !improved; p1++) {
              for (let p2 = 0; p2 < newTeams[t2].players.length && !improved; p2++) {
                const player1 = newTeams[t1].players[p1];
                const player2 = newTeams[t2].players[p2];
                
                // Simulate swap
                newTeams[t1].totalOCE = newTeams[t1].totalOCE - player1.oce + player2.oce;
                newTeams[t1].totalHeight = newTeams[t1].totalHeight - player1.height + player2.height;
                newTeams[t1].totalWeight = newTeams[t1].totalWeight - player1.weight + player2.weight;
                newTeams[t2].totalOCE = newTeams[t2].totalOCE - player2.oce + player1.oce;
                newTeams[t2].totalHeight = newTeams[t2].totalHeight - player2.height + player1.height;
                newTeams[t2].totalWeight = newTeams[t2].totalWeight - player2.weight + player1.weight;
                
                updateAverages(newTeams);
                const newImbalance = getCurrentImbalance();
                
                if (newImbalance < currentImbalance - 0.01) {
                  // Keep the swap
                  newTeams[t1].players[p1] = player2;
                  newTeams[t2].players[p2] = player1;
                  improved = true;
                } else {
                  // Revert the swap
                  newTeams[t1].totalOCE = newTeams[t1].totalOCE - player2.oce + player1.oce;
                  newTeams[t1].totalHeight = newTeams[t1].totalHeight - player2.height + player1.height;
                  newTeams[t1].totalWeight = newTeams[t1].totalWeight - player2.weight + player1.weight;
                  newTeams[t2].totalOCE = newTeams[t2].totalOCE - player1.oce + player2.oce;
                  newTeams[t2].totalHeight = newTeams[t2].totalHeight - player1.height + player2.height;
                  newTeams[t2].totalWeight = newTeams[t2].totalWeight - player1.weight + player2.weight;
                  updateAverages(newTeams);
                }
              }
            }
          }
        }
      }
    }
    
    // Final stats
    newTeams.forEach(team => {
      if (team.players.length > 0) {
        team.positions = {};
        team.players.forEach(p => {
          team.positions[p.position] = (team.positions[p.position] || 0) + 1;
        });
      }
    });
    
    console.log('[TeamBuilder] Generated teams:', newTeams.map(t => ({
      players: t.players.length,
      avgOCE: t.avgOCE?.toFixed(2),
      avgHeight: t.avgHeight?.toFixed(0),
      avgWeight: t.avgWeight?.toFixed(0)
    })));
    
    setTeams(newTeams);
    // Keep existing games - don't clear them!
    setCurrentGame(null);
  };

  // Move player between teams manually
  const movePlayer = (playerId, fromTeamIdx, toTeamIdx) => {
    if (fromTeamIdx === toTeamIdx) return;
    
    setTeams(prevTeams => {
      const newTeams = prevTeams.map(team => ({
        ...team,
        players: [...team.players]
      }));
      
      // Find and remove player from source team
      const playerIdx = newTeams[fromTeamIdx].players.findIndex(p => p.id === playerId);
      if (playerIdx === -1) return prevTeams;
      
      const [player] = newTeams[fromTeamIdx].players.splice(playerIdx, 1);
      
      // Add to target team
      newTeams[toTeamIdx].players.push(player);
      
      // Recalculate team stats
      newTeams.forEach(team => {
        team.totalScore = team.players.reduce((sum, p) => sum + p.score, 0);
        if (team.players.length > 0) {
          team.avgHeight = team.players.reduce((sum, p) => sum + p.height, 0) / team.players.length;
          team.avgWeight = team.players.reduce((sum, p) => sum + p.weight, 0) / team.players.length;
          team.avgOCE = team.players.reduce((sum, p) => sum + p.oce, 0) / team.players.length;
          team.positions = {};
          team.players.forEach(p => {
            team.positions[p.position] = (team.positions[p.position] || 0) + 1;
          });
        }
      });
      
      return newTeams;
    });
  };

  // Toggle player/criteria
  const togglePlayer = (playerId) => {
    // Check if it's a guest
    if (playerId.startsWith('guest_')) {
      setGuestPlayers(prev => prev.filter(g => g.tempId !== playerId));
    } else {
      setSelectedPlayers(prev => 
        prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
      );
    }
    setTeams([]);
  };

  // Add a new guest manually
  const addGuest = () => {
    if (!newGuestName.trim()) return;
    const newGuest = {
      tempId: `guest_manual_${Date.now()}`,
      name: newGuestName.trim(),
      invitedBy: null
    };
    setGuestPlayers(prev => [...prev, newGuest]);
    setNewGuestName('');
    setShowAddGuest(false);
    setTeams([]);
  };

  const toggleCriteria = (key) => {
    setCriteria(prev => ({ ...prev, [key]: !prev[key] }));
    setTeams([]);
  };

  // Add a game result - FIXED
  const addGame = (teamAIdx, teamBIdx, scoreA, scoreB) => {
    const sA = parseInt(scoreA);
    const sB = parseInt(scoreB);
    
    if (isNaN(sA) || isNaN(sB)) {
      alert('Bitte gültige Punktzahlen eingeben!');
      return;
    }
    
    if (sA === sB) {
      alert('Unentschieden nicht erlaubt!');
      return;
    }
    
    const newGame = {
      teamA: teamAIdx,
      teamB: teamBIdx,
      teamAPlayers: teams[teamAIdx].players.map(p => p.id),
      teamBPlayers: teams[teamBIdx].players.map(p => p.id),
      scoreA: sA,
      scoreB: sB,
      winner: sA > sB ? 'A' : 'B'
    };
    
    setGames(prev => [...prev, newGame]);
    setCurrentGame({ scoreA: '', scoreB: '' });
  };

  // Save all games via Backend API
  const saveGames = async () => {
    if (games.length === 0) {
      alert('Keine Spiele zum Speichern!');
      return;
    }
    
    setSavingGames(true);
    let savedCount = 0;
    
    try {
      for (const game of games) {
        const response = await fetch(`${BACKEND_URL}/api/training/games`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_id: eventId,
            team_a_players: game.teamAPlayers,
            team_b_players: game.teamBPlayers,
            team_a_score: game.scoreA,
            team_b_score: game.scoreB,
            winner: game.winner
          })
        });
        
        const result = await response.json();
        console.log('[TeamBuilder] Save game result:', result);
        
        if (result.ok) {
          savedCount++;
        } else {
          console.error('[TeamBuilder] Failed to save game:', result);
        }
      }
      
      if (savedCount === games.length) {
        alert(`✅ ${savedCount} Spiele gespeichert!`);
      } else {
        alert(`⚠️ ${savedCount}/${games.length} Spiele gespeichert`);
      }
      
      // Reload saved games from backend
      const response = await fetch(`${BACKEND_URL}/api/training/games/by_event/${eventId}`);
      const data = await response.json();
      if (data.ok && data.games) {
        setSavedGames(data.games);
      }
      
      setGames([]);
    } catch (err) {
      console.error('[TeamBuilder] Error saving games:', err);
      alert('❌ Fehler beim Speichern: ' + err.message);
    }
    
    setSavingGames(false);
  };

  // Delete a saved game (admin only)
  const deleteSavedGame = async (gameId) => {
    if (!user || role !== 'admin') {
      alert('Nur Admins können Spiele löschen');
      return;
    }
    
    if (!window.confirm('Dieses Spiel wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/training/games/${gameId}?admin_id=${user.uid}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      
      if (result.ok) {
        setSavedGames(prev => prev.filter(g => g.id !== gameId));
      } else {
        alert('Fehler beim Löschen');
      }
    } catch (err) {
      console.error('Error deleting game:', err);
      alert('Fehler beim Löschen');
    }
  };

  // Helper to get player name from ID
  const getPlayerNameById = (playerId) => {
    if (playerId?.startsWith('guest_')) {
      const guest = guestPlayers.find(g => g.tempId === playerId);
      return guest?.name || 'Gast';
    }
    const user = allUsers.find(u => u.id === playerId);
    return user?.display_name || user?.name || '?';
  };

  const getShortNameById = (playerId) => {
    const fullName = getPlayerNameById(playerId);
    return getShortName(fullName);
  };

  const availablePlayers = allUsers
    .filter(u => !selectedPlayers.includes(u.id) && (u.role === 'player' || u.role === 'sparring'))
    .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));

  const teamColors = ['bg-blue-600/30 border-blue-500/50', 'bg-red-600/30 border-red-500/50', 'bg-green-600/30 border-green-500/50'];
  const teamNames = ['Team Blau', 'Team Rot', 'Team Grün'];
  const teamTextColors = ['text-blue-300', 'text-red-300', 'text-green-300'];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
        <div className="text-white text-sm">Lade...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
      <div className="min-h-screen p-3">
        <div className="max-w-4xl mx-auto">
          {/* Header - COMPACT */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Team Builder</h2>
                <p className="text-xs text-rose-200/60">
                  {selectedPlayers.length} Spieler • {selectedPlayers.length >= 13 ? '3' : '2'} Teams
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={onClose} className="text-white border-white/20 text-xs px-2 py-1 h-7">
              ✕
            </Button>
          </div>

          {/* Criteria - COMPACT */}
          <div className="bg-[#1a0d11] border border-white/10 rounded-lg p-2 mb-2">
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: 'height', label: 'Größe', icon: Ruler },
                { key: 'weight', label: 'Gewicht', icon: Scale },
                { key: 'position', label: 'Pos', icon: Target },
                { key: 'oce', label: 'OCE', icon: TrendingUp },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => toggleCriteria(key)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                    criteria[key] ? 'bg-orange-600/30 border border-orange-500/50 text-orange-300' : 'bg-white/5 border border-white/10 text-rose-200/60'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {label}
                  {criteria[key] && <Check className="w-3 h-3" />}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Players - COMPACT with Guest support */}
          <div className="bg-[#1a0d11] border border-white/10 rounded-lg p-2 mb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white">Spieler ({playersWithData.length})</span>
              <div className="flex gap-2">
                <button onClick={() => setShowAddGuest(!showAddGuest)} className="flex items-center gap-1 text-[10px] text-blue-400">
                  <Plus className="w-3 h-3" /> +1 Gast
                </button>
                <button onClick={() => setShowAddPlayer(!showAddPlayer)} className="flex items-center gap-1 text-[10px] text-green-400">
                  <Plus className="w-3 h-3" /> Spieler
                </button>
              </div>
            </div>
            
            {/* Add Guest Input */}
            {showAddGuest && (
              <div className="flex gap-1 mb-2">
                <input
                  type="text"
                  value={newGuestName}
                  onChange={(e) => setNewGuestName(e.target.value)}
                  placeholder="Gastname..."
                  className="flex-1 px-2 py-1 text-xs rounded bg-blue-900/30 border border-blue-500/30 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && addGuest()}
                />
                <Button onClick={addGuest} disabled={!newGuestName.trim()} className="text-xs px-2 py-1 h-7 bg-blue-600">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            )}
            
            <div className="flex flex-wrap gap-1">
              {playersWithData.map(player => (
                <div key={player.id} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                  player.isGuest ? 'bg-blue-600/20 border border-blue-500/30 text-blue-300' :
                  player.hasData ? 'bg-white/10 text-white' : 'bg-yellow-900/30 text-yellow-300'
                }`}>
                  <span>{player.shortName}</span>
                  {player.isGuest && <span className="text-[10px]">(G)</span>}
                  <button onClick={() => togglePlayer(player.id)} className="text-red-400 hover:text-red-300">
                    <Minus className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Add Player - COMPACT */}
          {showAddPlayer && (
            <div className="bg-[#1a0d11] border border-blue-500/30 rounded-lg p-2 mb-2">
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {availablePlayers.map(player => (
                  <button key={player.id} onClick={() => togglePlayer(player.id)}
                    className="w-full flex items-center justify-between px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-xs">
                    <span className="text-white">{player.display_name || player.name}</span>
                    <Plus className="w-3 h-3 text-green-400" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* SAVED GAMES SECTION - Shows previously played games for this training */}
          {savedGames.length > 0 && (
            <div className="bg-[#1a0d11] border border-purple-500/30 rounded-lg p-2 mb-2">
              <button 
                onClick={() => setShowSavedGames(!showSavedGames)}
                className="w-full flex items-center justify-between mb-2"
              >
                <span className="text-sm font-bold text-purple-300 flex items-center gap-2">
                  <History className="w-4 h-4" /> 
                  Gespielte Spiele ({savedGames.length})
                </span>
                {showSavedGames ? <ChevronUp className="w-4 h-4 text-purple-300" /> : <ChevronDown className="w-4 h-4 text-purple-300" />}
              </button>
              
              {showSavedGames && (
                <div className="space-y-1">
                  {savedGames.map((game, idx) => (
                    <div key={game.id} className="bg-white/5 rounded-lg overflow-hidden">
                      {/* Game Header - clickable */}
                      <div 
                        className="flex items-center justify-between p-2 cursor-pointer hover:bg-white/10"
                        onClick={() => setExpandedGame(expandedGame === game.id ? null : game.id)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-purple-200/60">#{idx + 1}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-blue-300 text-xs">Blau</span>
                            <span className="font-bold text-white text-sm">{game.team_a_score}:{game.team_b_score}</span>
                            <span className="text-red-300 text-xs">Rot</span>
                          </div>
                          {game.winner === 'A' ? (
                            <span className="text-[10px] px-1 rounded bg-blue-600/30 text-blue-300">✓</span>
                          ) : (
                            <span className="text-[10px] px-1 rounded bg-red-600/30 text-red-300">✓</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {role === 'admin' && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); deleteSavedGame(game.id); }}
                              className="text-red-400 hover:text-red-300 p-1"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                          {expandedGame === game.id ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
                        </div>
                      </div>
                      
                      {/* Expanded: Show Teams */}
                      {expandedGame === game.id && (
                        <div className="p-2 pt-0 grid grid-cols-2 gap-2">
                          {/* Team A (Blau) */}
                          <div className="bg-blue-600/20 border border-blue-500/30 rounded p-1.5">
                            <div className="text-[10px] text-blue-300 font-bold mb-1">Team Blau ({game.team_a_players?.length || 0})</div>
                            <div className="space-y-0.5">
                              {(game.team_a_players || []).map(playerId => (
                                <div key={playerId} className="text-xs text-white/80">
                                  {getShortNameById(playerId)}
                                </div>
                              ))}
                            </div>
                          </div>
                          {/* Team B (Rot) */}
                          <div className="bg-red-600/20 border border-red-500/30 rounded p-1.5">
                            <div className="text-[10px] text-red-300 font-bold mb-1">Team Rot ({game.team_b_players?.length || 0})</div>
                            <div className="space-y-0.5">
                              {(game.team_b_players || []).map(playerId => (
                                <div key={playerId} className="text-xs text-white/80">
                                  {getShortNameById(playerId)}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Generate Button - COMPACT */}
          <Button onClick={generateTeams} disabled={selectedPlayers.length < 4}
            className="w-full mb-3 py-2 text-sm bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 disabled:opacity-50">
            <Shuffle className="w-4 h-4 mr-1" />
            {teams.length > 0 ? 'Neue Teams generieren' : 'Teams generieren'}
          </Button>

          {/* Generated Teams */}
          {teams.length > 0 && (
            <div className="space-y-2">
              {/* Teams Grid - COMPACT with OCE */}
              <div className={`grid gap-2 ${teams.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {teams.map((team, idx) => (
                  <div key={idx} className={`${teamColors[idx]} border rounded-lg p-2`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-white text-sm">{teamNames[idx]}</span>
                      <span className="text-[10px] text-white/50">{team.players.length}p</span>
                    </div>
                    
                    {/* Stats including OCE */}
                    <div className="flex gap-1 text-[10px] mb-1.5 bg-black/20 rounded p-1 flex-wrap">
                      <span className="text-white">{team.avgHeight?.toFixed(0) || 0}cm</span>
                      <span className="text-rose-200/30">|</span>
                      <span className="text-white">{team.avgWeight?.toFixed(0) || 0}kg</span>
                      <span className="text-rose-200/30">|</span>
                      <span className="text-orange-300">OCE {team.avgOCE?.toFixed(1) || 0}</span>
                    </div>
                    
                    {/* Players with move buttons always visible on mobile */}
                    <div className="space-y-0.5">
                      {team.players.map((player) => (
                        <div key={player.id} className="flex items-center justify-between px-1.5 py-1 rounded bg-white/10 text-xs">
                          <span className={`text-white truncate ${player.isGuest ? 'italic text-blue-300' : ''}`}>
                            {player.shortName}
                            {player.isGuest && ' (G)'}
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-rose-200/50">{POSITION_NAMES[player.position] || '?'}</span>
                            {/* Move buttons - always visible */}
                            <div className="flex gap-0.5">
                              {teams.map((_, toIdx) => {
                                if (toIdx === idx) return null;
                                return (
                                  <button 
                                    key={toIdx} 
                                    onClick={() => movePlayer(player.id, idx, toIdx)}
                                    className={`px-1.5 py-0.5 rounded text-[10px] ${
                                      toIdx === 0 ? 'bg-blue-600/50 text-blue-200' : 
                                      toIdx === 1 ? 'bg-red-600/50 text-red-200' : 
                                      'bg-green-600/50 text-green-200'
                                    }`}
                                  >
                                    →{toIdx === 0 ? 'B' : toIdx === 1 ? 'R' : 'G'}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Game Entry - FIXED */}
              <div className="bg-[#1a0d11] border border-yellow-500/30 rounded-lg p-2">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4 text-yellow-300" />
                  <span className="text-sm font-bold text-yellow-300">Spiel eintragen</span>
                </div>
                
                {teams.length === 2 && (
                  <div className="flex items-center justify-center gap-2">
                    <div className="text-center">
                      <div className={`text-xs ${teamTextColors[0]} mb-1`}>{teamNames[0]}</div>
                      <input
                        type="number" min="0" placeholder="0"
                        className="w-14 p-1.5 rounded bg-blue-900/30 border border-blue-500/30 text-center text-white text-lg font-bold"
                        value={currentGame?.scoreA || ''}
                        onChange={(e) => setCurrentGame({ ...currentGame, scoreA: e.target.value })}
                        data-testid="score-team-a"
                      />
                    </div>
                    <span className="text-xl text-white font-bold">:</span>
                    <div className="text-center">
                      <div className={`text-xs ${teamTextColors[1]} mb-1`}>{teamNames[1]}</div>
                      <input
                        type="number" min="0" placeholder="0"
                        className="w-14 p-1.5 rounded bg-red-900/30 border border-red-500/30 text-center text-white text-lg font-bold"
                        value={currentGame?.scoreB || ''}
                        onChange={(e) => setCurrentGame({ ...currentGame, scoreB: e.target.value })}
                        data-testid="score-team-b"
                      />
                    </div>
                    <Button
                      onClick={() => addGame(0, 1, currentGame?.scoreA, currentGame?.scoreB)}
                      disabled={!currentGame?.scoreA || !currentGame?.scoreB}
                      className="bg-yellow-600 hover:bg-yellow-700 text-xs px-3 py-1.5 h-8"
                      data-testid="add-game-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Spiel
                    </Button>
                  </div>
                )}
                  
                {/* For 3 teams - COMPACT */}
                {teams.length === 3 && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        className="p-1.5 text-xs rounded bg-white/10 border border-white/20 text-white"
                        value={currentGame?.teamA ?? ''}
                        onChange={(e) => setCurrentGame({ ...currentGame, teamA: e.target.value ? parseInt(e.target.value) : undefined })}
                      >
                        <option value="">Team A</option>
                        {teams.map((_, idx) => (
                          <option key={idx} value={idx} disabled={idx === currentGame?.teamB}>{teamNames[idx]}</option>
                        ))}
                      </select>
                      <select
                        className="p-1.5 text-xs rounded bg-white/10 border border-white/20 text-white"
                        value={currentGame?.teamB ?? ''}
                        onChange={(e) => setCurrentGame({ ...currentGame, teamB: e.target.value ? parseInt(e.target.value) : undefined })}
                      >
                        <option value="">Team B</option>
                        {teams.map((_, idx) => (
                          <option key={idx} value={idx} disabled={idx === currentGame?.teamA}>{teamNames[idx]}</option>
                        ))}
                      </select>
                    </div>
                    
                    {currentGame?.teamA !== undefined && currentGame?.teamB !== undefined && (
                      <div className="flex items-center justify-center gap-2">
                        <input type="number" min="0" placeholder="0"
                          className="w-12 p-1.5 rounded bg-white/10 border border-white/20 text-center text-white text-lg font-bold"
                          value={currentGame?.scoreA || ''}
                          onChange={(e) => setCurrentGame({ ...currentGame, scoreA: e.target.value })}
                        />
                        <span className="text-white font-bold">:</span>
                        <input type="number" min="0" placeholder="0"
                          className="w-12 p-1.5 rounded bg-white/10 border border-white/20 text-center text-white text-lg font-bold"
                          value={currentGame?.scoreB || ''}
                          onChange={(e) => setCurrentGame({ ...currentGame, scoreB: e.target.value })}
                        />
                        <Button
                          onClick={() => addGame(currentGame?.teamA, currentGame?.teamB, currentGame?.scoreA, currentGame?.scoreB)}
                          disabled={!currentGame?.scoreA || !currentGame?.scoreB}
                          className="bg-yellow-600 hover:bg-yellow-700 text-xs px-2 py-1 h-8"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Recorded Games - COMPACT */}
                {games.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="text-xs text-white/60">Spiele ({games.length}):</div>
                    {games.map((game, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white/5 rounded p-1.5 text-xs">
                        <span className={teamTextColors[game.teamA]}>{teamNames[game.teamA].split(' ')[1]}</span>
                        <span className="font-bold text-white">{game.scoreA}:{game.scoreB}</span>
                        <span className={teamTextColors[game.teamB]}>{teamNames[game.teamB].split(' ')[1]}</span>
                        <button onClick={() => setGames(games.filter((_, i) => i !== idx))} className="text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    
                    <Button onClick={saveGames} disabled={savingGames} className="w-full mt-2 bg-green-600 hover:bg-green-700 text-xs py-1.5" data-testid="save-games-btn">
                      <Save className="w-3 h-3 mr-1" />
                      {savingGames ? 'Speichern...' : `${games.length} Spiele speichern`}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
